import { addIntegration, getClient } from '@sentry/nextjs'
import type { replayIntegration } from '@sentry/nextjs'
import type { AnalyticsMode } from '@/lib/analytics/consent'

/*
 * Sentry session replay follows the analytics consent (lib/analytics/consent.ts):
 * a replay records the screen, so it runs only in `full` mode, the same rule
 * that turns on PostHog's recordings.
 *
 * The replay code is not in the page bundle. It is imported here on demand
 * the first time a page load is in `full` mode, so visitors who never said
 * yes never download it. Sampling is the one set in `Sentry.init`
 * (lib/observability/sentry-client.ts): 1 % of sessions, and the moments
 * before an error in every session that hits one. Text, inputs and media are
 * masked.
 */

type Replay = ReturnType<typeof replayIntegration>

/** The mode the latest call asked for; a slow import checks it before adding. */
let wanted: AnalyticsMode = 'anonymous'
let loading: Promise<void> | null = null
/** Replay was stopped by a withdrawal in this page load. */
let stopped = false

/** Read without importing the replay package (`Sentry.getReplay` would). */
function currentReplay(): Replay | undefined {
  return getClient()?.getIntegrationByName<Replay>('Replay')
}

function sentryIsRunning(): boolean {
  const options = getClient()?.getOptions()
  return !!options?.dsn && options.enabled !== false
}

/**
 * Starts or stops Sentry replay for the analytics mode. Safe to call on every
 * mode change: it loads the integration once and never runs it without
 * consent.
 */
export function syncSentryReplay(mode: AnalyticsMode): Promise<void> {
  wanted = mode

  if (mode !== 'full') {
    // `flush: false` drops the pending recording, so nothing captured with
    // consent leaves the browser after it was withdrawn.
    const replay = currentReplay()
    if (replay && !stopped) {
      stopped = true
      void replay.stop({ flush: false }).catch(() => undefined)
    }
    return Promise.resolve()
  }

  if (!sentryIsRunning()) return Promise.resolve()

  const replay = currentReplay()
  if (replay) {
    // Consent given again after a withdrawal in the same page load: keep only
    // the error buffer, not a full recording.
    if (stopped) {
      stopped = false
      replay.startBuffering()
    }
    return Promise.resolve()
  }

  loading ??= import('./sentry-replay-integration')
    .then(({ createReplayIntegration }) => {
      if (wanted !== 'full' || currentReplay()) return
      addIntegration(createReplayIntegration())
    })
    .catch(() => {
      // Chunk failed to load (offline, blocked): no replay this page load.
    })
    .finally(() => {
      loading = null
    })
  return loading
}
